<template>
  <div
    class="home-banner-frame__grid"
    :class="`home-banner-frame__grid--${layoutMode}`"
  >
    <button
      v-for="pos in visiblePositions"
      :key="pos"
      type="button"
      class="home-banner-frame__cell"
      :class="[
        `home-banner-frame__cell--pos-${pos}`,
        { 'home-banner-frame__cell--main': pos === mainPosition },
      ]"
      :disabled="!slotMap[pos] || !clickable"
      @click="onCellClick(pos)"
    >
      <img
        v-if="slotMap[pos]"
        v-show="loadImages"
        :src="getBannerImageUrl(slotMap[pos], { position: pos, layoutMode })"
        :alt="getBannerTitle(slotMap[pos])"
        class="home-banner-frame__cell-img"
        :loading="resolveImageLoading(pos)"
        decoding="async"
        :fetchpriority="resolveFetchPriority(pos)"
        @load="onImageLoad(pos)"
        @error="onImageError(pos, $event)"
      />
    </button>

    <!-- 分页点独立于格位 button，避免嵌套 button 导致移动端点击失效 -->
    <div
      v-if="showDots"
      class="home-banner-frame__dots-layer"
    >
      <div
        class="home-banner-frame__dots"
        role="tablist"
        aria-label="轮播分页"
      >
        <button
          v-for="(_, index) in slideCount"
          :key="index"
          type="button"
          class="home-banner-frame__dot"
          :class="{ 'home-banner-frame__dot--active': index === activeIndex }"
          :aria-label="`第 ${index + 1} 屏`"
          :aria-selected="index === activeIndex"
          @pointerup.stop="onDotActivate(index, $event)"
          @keydown.enter.prevent="onDotActivate(index)"
          @keydown.space.prevent="onDotActivate(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import {
  BANNER_H5_POSITION,
  BANNER_MAIN_POSITION,
  BANNER_POSITIONS,
  getBannerImageUrl,
  getBannerTitle,
} from '@/features/home/utils/bannerLink'
import { bannerDebug } from '@/features/home/utils/bannerDebug'

const props = defineProps({
  /** 格位映射（勿用 slots，与 Vue 保留名冲突） */
  slotMap: {
    type: Object,
    required: true,
  },
  /** web=PC 8 宫格，h5=移动端全宽单图 */
  layoutMode: {
    type: String,
    default: 'web',
    validator: (value) => value === 'web' || value === 'h5',
  },
  clickable: {
    type: Boolean,
    default: true,
  },
  showDots: {
    type: Boolean,
    default: false,
  },
  slideCount: {
    type: Number,
    default: 0,
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  /** 是否挂载 img 请求图片（轨道按需加载） */
  loadImages: {
    type: Boolean,
    default: true,
  },
  /** 当前静止展示的真实屏 */
  isActiveFrame: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['comic-click', 'go-to-slide'])

const visiblePositions = computed(() =>
  props.layoutMode === 'h5' ? [BANNER_H5_POSITION] : BANNER_POSITIONS
)

const mainPosition = computed(() =>
  props.layoutMode === 'h5' ? BANNER_H5_POSITION : BANNER_MAIN_POSITION
)

/**
 * @param {number} pos
 * @returns {'eager' | 'lazy'}
 */
function resolveImageLoading(pos) {
  if (props.isActiveFrame || props.loadImages) return 'eager'
  return 'lazy'
}

/**
 * @param {number} pos
 * @returns {'high' | 'low' | 'auto'}
 */
function resolveFetchPriority(pos) {
  if (!props.isActiveFrame) return 'auto'
  if (pos === mainPosition.value) return 'high'
  return 'low'
}

/**
 * @param {number} pos
 */
function onCellClick(pos) {
  if (!props.slotMap[pos] || !props.clickable) return
  emit('comic-click', props.slotMap[pos])
}

/** 去重：避免 pointerup + 合成 click 重复切屏 */
let lastDotActivateIndex = -1
let lastDotActivateAt = 0

/**
 * 分页点切屏：pointerup 触摸/鼠标即时响应，避免 touchend+click 双绑定的 ~300ms 延迟
 * @param {number} index
 * @param {PointerEvent} [event]
 */
function onDotActivate(index, event) {
  if (event?.pointerType === 'mouse' && event.button !== 0) return

  const now = Date.now()
  if (index === lastDotActivateIndex && now - lastDotActivateAt < 400) return
  lastDotActivateIndex = index
  lastDotActivateAt = now

  emit('go-to-slide', index)
}

watch(
  () => props.slotMap,
  (slotMap) => {
    if (!slotMap) return
    const filled = BANNER_POSITIONS.filter((pos) => Boolean(slotMap[pos]))
    bannerDebug('frame-slot-map', {
      clickable: props.clickable,
      filledPositions: filled,
      filledCount: filled.length,
      pos1: slotMap[1] ? { name: slotMap[1].name, coverUrl: slotMap[1].coverUrl } : null,
      pos4: slotMap[4] ? { name: slotMap[4].name, coverUrl: slotMap[4].coverUrl } : null,
    })
  },
  { immediate: true, deep: true }
)

/** @param {number} pos */
function onImageLoad(pos) {
  bannerDebug('image-load', { pos })
}

/**
 * @param {number} pos
 * @param {Event} event
 */
function onImageError(pos, event) {
  const target = /** @type {HTMLImageElement|null} */ (event.target)
  bannerDebug('image-error', {
    pos,
    src: target?.src ?? '',
  })
}
</script>

<style scoped src="@/features/home/styles/HomeBannerFrame.css"></style>
